import React from 'react';
import { Link } from 'react-router-dom';

import { PATHS, FULL_PATHS, HASHES } from '../../../../constants';

export const releaseNotesContent: ReleaseNote[] = [
    {
        version: '7.1.3',
        date: 'February 2, 2024',
        new: [
            <p key="add-detections-duration-options">
                {`We added a two more duration options to the Detections table, so
                that you can now filter by durations of >3s, >5s, >9s, >14s and >24s.`}
            </p>,
        ],
    },
    {
        version: '7.1.2',
        date: 'July 20, 2023',
        new: [
            <p key="add-detections-search-filter">
                We added a search filter on the Detection Settings table so that
                you can easily find your detection tracks.
            </p>,
        ],
    },
    {
        version: '7.1.1',
        date: 'June 10, 2023',
        fixes: [
            <p key="confidence-filter-update">
                We’ve updated the Confidence filter on the Detections Table.
                Previously, the Confidence filter would only include detections
                with the selected confidence, for example, &ldquo;Medium.&rdquo;
                Now, the filter includes detections with the selected confidence
                and detections with any higher confidence, effectively
                functioning as a &ldquo;greater than or equal to&rdquo; filter.
                This means a filter selection of &ldquo;&gt;= Medium&rdquo;
                includes detections with both Medium and High confidence.
            </p>,
        ],
    },
    {
        version: '7.1.0',
        date: 'May 21, 2023',
        new: [
            <div key="detections-table-new-features">
                <p>
                    We’ve added some new exciting new features to the Detections
                    table!
                </p>
                <h4>New Columns</h4>
                <ul className="mt-2 ml-6 list-disc">
                    <li>
                        <span className="font-semibold">Confidence</span> gives
                        you a score of Low, Medium or High to indicate the
                        confidence of the detection.
                    </li>
                    <li>
                        <span className="font-semibold">Country</span> lists the
                        two-letter country code for the detection.
                    </li>
                </ul>
                <h4>New Filters</h4>
                <p>
                    To support these two new columns, we’ve added filters for{' '}
                    <span className="font-semibold">Confidence</span> and{' '}
                    <span className="font-semibold">Country</span> to the
                    Detections table.
                </p>
                <p>
                    We also added a previously missing filter for{' '}
                    <span className="font-semibold">Duration</span>, which
                    allows you to quickly filter by selections including:
                    <ul className="mt-2 ml-6 list-disc">
                        <li>All Durations</li>
                        <li>{'>3s'}</li>
                        <li>{'>5s'}</li>
                        <li>{'>9s'}</li>
                    </ul>
                </p>
                <h4>Filtered Downloads</h4>
                <p>
                    Previously, when you clicked the{' '}
                    <span className="font-semibold">Download As...</span> link
                    above the Detections table, the CSV would contain all your
                    detections for the current time period, regardless of your
                    filter settings. Now, the{' '}
                    <span className="font-semibold">Download As...</span> link
                    is aware of your filter settings, and only downloads the
                    detection data currently shown in the table.
                </p>
            </div>,
        ],
    },
    {
        version: '7.0.0',
        date: 'March 7, 2023',
        new: [
            <p key="detection-reference">
                We’ve added a <span className="font-semibold">Reference</span>{' '}
                column to the Detections table which links to the video or audio
                context where your work was detected. This allows you to confirm
                the accuracy of your detections from right within TrqkIQ™.
            </p>,
            <p key="detection-download">
                We’ve also added a{' '}
                <span className="font-semibold">Download As...</span> link above
                the Detections table which allows you to download a CSV of your
                detection data for the date range currently being viewed.
            </p>,
        ],
    },
    {
        version: '6.3.1',
        date: 'August 29, 2022',
        fixes: [
            <p key="fix-period-tooltip">
                We’ve corrected an error in our explanation of performance
                periods vs. statement periods. ASCAP usually sends out their
                statements two quarters after the performance period, not three
                as we had stated before.
            </p>,
        ],
    },
    {
        version: '6.3.0',
        date: 'August 15, 2022',
        new: [
            <p key="gap-analysis-updates-3">
                In Publisher Edition, we’ve added a few more updates to Gap
                Analysis:
                <ul className="ml-6">
                    <li>
                        When confirming the list of works to include in a Gap
                        Analysis Report, if more than one detected audio file is
                        matched to the same statement work, the matches are
                        grouped together.
                    </li>
                    <li>
                        Similarly, when viewing a Gap Analysis Report in the
                        application, if more than one of the detected audio
                        files included in the report is matched to the same
                        statement work, the matches are summarized into a single
                        line.
                    </li>
                    <li>
                        When setting up the channels for a Gap Analysis Report,
                        you’ll now see a count of the matched performances vs.
                        the total for each channel.
                    </li>
                </ul>
            </p>,
        ],
    },
    {
        version: '6.2.0',
        date: 'June 24, 2022',
        new: [
            <p key="gap-analysis-updates-2">
                In Publisher Edition, we’ve added more improvements to the Gap
                Analysis workflow:
                <ul className="ml-6">
                    <li>
                        You can now use a &ldquo;Select All&rdquo; checkbox to
                        select all the channels in a Gap Analysis Report
                    </li>
                    <li>
                        We’ve added a &ldquo;Delete&rdquo; button to the list of
                        Gap Analysis reports so you can keep things clean.
                    </li>
                </ul>
            </p>,
        ],
        fixes: [
            <p key="faq-links">
                In Publisher Edition, the Gap Analysis workflow now includes
                links to our{' '}
                <a
                    href="https://www.trqk.io/faq"
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                >
                    FAQ
                </a>{' '}
                that help explain the finer details.
            </p>,
            <p key="period-clarifications">
                We’ve added clarifying text to make it clear when a label refers
                to a distribution period vs. a performance period.
            </p>,
        ],
    },
    {
        version: '6.1.0',
        date: 'June 6, 2022',
        new: [
            <p key="gap-analysis-updates">
                In Publisher Edition, we’ve added a few key improvements to the
                Gap Analysis workflow:
                <ul className="ml-6">
                    <li>
                        Most gap analysis channels are now pre-processed,
                        speeding up the process of creating a report
                    </li>
                    <li>
                        You can now use a &ldquo;Select All&rdquo; checkbox to
                        select all the works for a given channel
                    </li>
                    <li>
                        We’ve added a &ldquo;Download As...&rdquo; button so you
                        can enter your own filename when downloading a Gap
                        Analysis Report
                    </li>
                </ul>
            </p>,
        ],
        fixes: [
            <p key="update-gap-analysis-error-messaging">
                In Publisher Edition, we’ve updated our messaging for Gap
                Analysis Reports that contain no detections. The new text
                clarifies that this state can can be caused by your filter
                selections.
            </p>,
        ],
    },
    {
        version: '6.0.0',
        date: 'April 18, 2022',
        new: [
            <p key="large-statement-support">
                We’ve improved our ability to ingest large PRO statements.
            </p>,
            <p key="tunesat-support">
                Publisher Edition now also supports the import of TuneSat
                detection data for Gap Analysis.
            </p>,
        ],
    },
    {
        version: '5.0.2',
        date: 'February 2, 2022',
        fixes: [
            <p key="fix-channel-search">
                We’ve fixed a bug in the channel search feature of the Confirm
                Channels page of Gap Analysis.
            </p>,
        ],
    },
    {
        version: '5.0.1',
        date: 'January 6, 2022',
        fixes: [
            <p key="fix-channel-selection">
                When creating or editing a Gap Analysis Report the checkboxes
                for each channel in the Confirm Channels page now behave as
                expected. Previously, these checkboxes would be inadvertently
                reset to an un-checked state when using the Back button from the
                Confirm Works page.
            </p>,
        ],
    },
    {
        version: '5.0.0',
        date: 'November 19, 2021',
        new: [
            <p key="publisher-edition">
                We are proud to bring you TrqkIQ™ Publisher Edition, featuring
                Gap Analysis reporting. Gap Analysis allows you to compare
                performances reported by your PRO vs. detections of your on-air
                tracks. You can upload detection data from your existing BMAT
                account to generate these reports, then download them in Excel
                format to use the supporting data in claims for missing
                royalties. Along with Gap Analysis, Publisher Edition also
                features unlimited royalty capacity, allowing you to upload as
                many statements as you like, and more billing options, including
                invoicing, with payment terms tailored to fit your needs.
            </p>,
            <p key="indie-edition">
                Current TrqkIQ™ users already have access to the powerful
                features of TrqkIQ™ Indie Edition, including real-time
                performance detection backed by the best-in-class ACR services
                of BMAT. Gap Analysis is coming soon for Indie Edition users,
                and will include the ability to analyze the gaps between Trqk
                detection data vs. statement performances.
            </p>,
        ],
        fixes: [
            <p key="manage-plan-button">
                Indie Edition users who have exceeded their detection track
                capacity will now be able to add additional detection tracks via
                the Manage Plan button. Previously, this button was not
                available.
            </p>,
        ],
    },
    {
        version: '4.1.2',
        date: 'October 5, 2021',
        new: [
            <p key="detection-notifications">
                You can now{' '}
                <Link
                    className="underline"
                    to={({ search }) => ({
                        search,
                        pathname: PATHS.SETTINGS,
                        hash: HASHES.SETTINGS.NOTIFICATIONS,
                    })}
                >
                    enable detection notifications
                </Link>{' '}
                to receive emails when Trqk detects performances of your audio
                files! You can set the frequency of these notifications to meet
                your needs.
            </p>,
        ],
    },
    {
        version: '4.1.1',
        date: 'August 27, 2021',
        new: [
            <p key="detections-table">
                We’ve added production company names to our detection data. That
                means you can now filter and sort by Production Company in the{' '}
                <Link
                    className="underline"
                    to={({ search }) => ({
                        search,
                        pathname: PATHS.DETECTIONS,
                    })}
                >
                    Detections table
                </Link>{' '}
                .
            </p>,
            <p key="top-production-companies">
                There’s a new Insight card for{' '}
                <span className="font-bold">Top Production Companies</span>{' '}
                which contains a list of production companies with the most
                detections in the past year. Each row links to a filtered view
                of the Detections table to reveal the supporting data.
            </p>,
            <div key="new-insight-cards">
                <p>We’ve also added two more new Insight cards:</p>
                <ol className="my-2 ml-6 list-decimal">
                    <li>
                        <span className="font-bold">Top Writers</span> contains
                        a list of writers with the highest earnings for the
                        current entity over the past four statements.
                    </li>
                    <li>
                        <span className="font-bold">Top Publishers</span>{' '}
                        contains a list of publishers (as well as
                        sub-publishers) with the highest earnings for the
                        current entity over the past four statements.
                    </li>
                </ol>
                <p>
                    Both of these Insight cards contain links within each row
                    which bring you to a filtered view of the Performance Data
                    table to reveal the supporting data.
                </p>
            </div>,
        ],
        fixes: [
            'Dropdown options within search filters are now sorted alphabetically.',
            'Recommended payment plans are now aware of your current detection usage.',
        ],
    },
    {
        version: '4.1.0',
        date: 'August 10, 2021',
        new: [
            'We have reorganized our payment plans to make it easier to find the right plan for your needs. You’ll now be able to select from several plans that are more tailored to your individual needs.',
            'Audio files can now be deleted from the Detection Manager, which should allow for more flexibility within your payment plan. Audio file deletion will have consequences with regards to your current data; a warning is included to clarify the details.',
        ],
        fixes: [
            'You will now see an alert if you are using an unsupported browser.',
        ],
    },
    {
        version: '4.0.2',
        date: 'July 30, 2021',
        new: [
            <p key="performances-data-table">
                The{' '}
                <Link
                    className="underline"
                    to={({ search }) => ({
                        search,
                        pathname: FULL_PATHS.STATEMENTS_TABLE,
                    })}
                >
                    Performance Data table
                </Link>{' '}
                has an improved Advanced Search mode. You can use this to filter
                your performance data by program, episode, work title, and more.
            </p>,
            'New links have been added to the Insights page in the Top Earning Programs and Bump & Falloff sections. These links lead back to the Performance Data table, using the advanced search filters, to show you the supporting data for a given Insight.',
            'You can now use TrqkIQ™ in multiple browser tabs, and each tab will effectively work like a separate running application. This is handy when you want to cross-compare different sections of the app, or use the app as two separate entities, for example.',
        ],
        fixes: [
            <p key="links">
                TrqkIQ™ links will now work across logins, allowing you to share
                links with your team. For example, if you send someone a link to{' '}
                <a
                    className="underline"
                    href="https://iq.trqk.io/reports#total-royalties"
                >
                    https://iq.trqk.io/reports#total-royalties
                </a>
                , they will see the Total Royalties report after they log in.
                Previously, this did not work if the link contained a hash sign.
            </p>,
            'The Works page now displays all of your works, even for entities with very large catalogs. Previously, some users would not see their entire list of works in some entities.',
        ],
    },
    {
        version: '4.0.1',
        date: 'July 22, 2021',
        new: [
            'We’ve added a reminder to let users know that detection is not enabled by default. This should help prevent unexpected gaps in detection history when moving from a free trial to a payment plan.',
        ],
        fixes: [
            'Added support for Safari version 11 and up; previously, the app would not load for some Safari users.',
            'Fixed a browser compatibility issue in formatting numbers, which would cause the app to crash for some users.',
        ],
    },
    {
        version: '4.0',
        date: 'July 6, 2021',
        new: [
            'We are now live!  After a multi-year engineering effort, multiple beta drops, and a ton of great feedback from hundreds of demos and conversations, we are excited to announce the full commercial launch of TrqkIQ™, our Music Royalty Intelligence platform!',
            'To support further innovation and improvements, you’ll be asked to upgrade from your free trial to either our Basic Plan or Pro Plan.  Our Basic Plan will remain free, with some limitations, and our Pro Plan pricing is based on usage. Under the Settings page, you’ll see our Payment Plan section which allows you to update your plan and payment settings.',
            <p key="detection">
                Our detection system is back online and now supports a total of
                200 broadcast TV and major radio stations. You’ll see detections
                start to show up for your enabled tracks over the first few days
                after you sign up, assuming they are on-air.
                <p className="ml-4">
                    <strong>Important</strong>: When you upload a track,
                    detection is turned <strong>Off</strong> by default. If you
                    were using our beta trial, detection has been turned off for
                    all of your tracks as part of the upgrade process. Within
                    the Detections page, click on “Manage / Add” and make sure
                    you flip the switch <strong>On</strong> for the tracks you
                    want to detect. Detection will remain on for a given track
                    until you turn it off again or your payment plan or trial
                    expires.
                </p>
            </p>,
            'We’ve added a new Reports page, which includes reports for Total Royalties and Gap Analysis. Total Royalties shows the sum of the earnings from each of your statements across all of your entities. Gap Analysis shows the difference between performances reported on your PRO statements and performances that were discovered by our detection system.',
        ],
        fixes: [
            'Error handling logic for statement uploads has been fixed; previously, the notification would look successful even if one or more statements failed to upload.',
            'You’ll always see the Detection Summary card in the Insights page if you have uploaded and enabled detection on at least one audio file. Previously, the Detection Summary card was only shown if you had also uploaded at least one statement.',
        ],
    },
    {
        version: '3.24',
        date: 'April 5, 2021',
        new: [
            'TrqkIQ™ now supports a much richer set of data types, representing collectively what we call “bulk payments”. We now distinguish cue sheets, adjustments, proxies, sample surveys, and settlements from single-performance payments. This allows you to analyze your royalties on a much more granular basis.',
            'TrqkIQ™ now accurately identifies and analyzes payments for cue sheets, allowing you to view royalties by show and episode. To underscore this distinction, the Tracks page has been renamed to the Works page, as it shows both tracks and cue sheets.',
            "The statement ingestion process is more stable than ever, and we've added a few tabs to the statement manager to help you keep organized as you upload multiple statements. We now have an “Uploads” tab, a “Complete” tab, and an “Alerts” tab. The Alerts tab keeps a list of the statements that didn't successfully upload, so that you have immediate access to download them and help us diagnose issues as we continue to improve our ingestion process.",
            'We have reorganized the detection manager to work similarly to the statement manager. The “Detection Tracks” tab allows you to toggle detection on or off for a given track. Current audio uploads show up in the “Uploads” tab, and any issues with audio uploads will show up in the “Alerts” tab.',
            'A search bar has been added to the Statements > Performance Data table, and the filter logic has been revised and improved.',
        ],
        fixes: [
            'Statement selection in the work detail page is now separate from statement selection in the rest of the app.',
            'Playback for AIFF files is now supported in all browsers.',
            'Channel payout ranges in the work detail page are now rendered correctly - previously, some ranges were erroneously removed from the charts.',
        ],
    },
    {
        version: '3.23.5',
        date: 'January 22, 2021',
        new: [
            'The Manage Statements modal has been rebuilt and improved, and is now organized by entity. Each row at the top level represents an entity, and can be expanded to show the statements within that entity.',
        ],
        fixes: [
            'Statement selection, especially after adding or deleting statements, is generally more stable.',
            "We've made some improvements in our support for international statements which should result in fewer ingestion failures.",
        ],
    },
];
